import * as contentful from 'contentful';

export interface BlogCategory {
  name: string;
  slug: string;
  children: ReadonlyArray<contentful.Entry<BlogCategory>>;
}

export type BlogCategoryEntry = contentful.Entry<BlogCategory>;

export interface MyLanding {
  title: string;
  slug: string;
  description: string;
  content: Document;

  show_search: boolean | undefined;
  country: string;
  state: string;
  destination: string;
}

export type LandingEntry = contentful.Entry<MyLanding>;

// Contentful image interface for blog posts and other content
export interface BlogPostPic {
  title: string;
  description: string;
  file: {
    url: string;
    details: {
      image: {
        height: number;
        width: number;
      };
      size: number;
    };
  };
}

// Constants for CDN domains
const CONTENTFUL_CDN_DOMAIN = 'images.ctfassets.net';
const BUNNY_CDN_DOMAIN = 'contentful-assets.b-cdn.net';

// Utility function to convert Contentful URLs to BunnyCDN URLs
// BunnyCDN acts as a CDN for contentful images, reducing API calls
const getBunnyCDNUrl = (contentfulUrl: string): string => {
  // Handle both formats: "//images.ctfassets.net" and "https://images.ctfassets.net"
  const fullUrl = contentfulUrl.startsWith('//') ? `https:${contentfulUrl}` : contentfulUrl;

  // Replace Contentful CDN domain with BunnyCDN pull zone
  return fullUrl.replace(CONTENTFUL_CDN_DOMAIN, BUNNY_CDN_DOMAIN);
};

// Enhanced image URL generator with BunnyCDN optimization
export const getOptimizedContentfulImageUrl = (
  contentfulUrl: string,
  width: number,
  height: number,
  format: 'webp' | 'jpg' | 'png' = 'webp',
  fit: 'thumb' | 'fill' | 'scale-down' = 'thumb',
): string => {
  const bunnyUrl = getBunnyCDNUrl(contentfulUrl);
  return `${bunnyUrl}?fm=${format}&w=${width}&h=${height}&fit=${fit}`;
};

// Generate responsive image URLs for different screen densities
export const getResponsiveContentfulImageUrls = (
  contentfulUrl: string,
  baseWidth: number,
  baseHeight: number,
  format: 'webp' | 'jpg' | 'png' = 'webp',
  fit: 'thumb' | 'fill' | 'scale-down' = 'thumb',
): string[] => {
  const urls: string[] = [];

  // Generate URLs for different scales (1x, 1.5x, 2x, 3x)
  for (const scale of [1, 1.5, 2, 3]) {
    const width = Math.round(baseWidth * scale);
    const height = Math.round(baseHeight * scale);
    urls.push(getOptimizedContentfulImageUrl(contentfulUrl, width, height, format, fit));
  }

  return urls;
};

export class ContentfulApiClient {
  private static readonly client = contentful.createClient({
    space: '3ja0qb1g0ubk',
    accessToken: 'S-8k4C6GPtyykaL2FEKZHfHuqh59uz5m_seu79jALw0',
  });

  public static async GetCategories(): Promise<ReadonlyArray<BlogCategoryEntry>> {
    const collection = await ContentfulApiClient.client.getEntries<BlogCategory>({
      content_type: 'blogCategory',
      include: 5,
    });

    return collection.items;
  }

  public static async GetLandings(): Promise<LandingEntry[]> {
    const entries = await ContentfulApiClient.client.getEntries<MyLanding>({
      content_type: 'landingPage',
      select: 'sys.id,fields.title,fields.slug,fields.country',
      // setting hardocoded limit to 1000
      limit: 1000,
    });
    return entries.items;
  }
}
